import { LucideFrown } from 'lucide-react';

import {
  InventoryItemsModalMode,
  InventoryTagData,
} from './_InventoryItemsModal';
import { InventoryItemsModalGroupsGrid } from './InventoryItemsModalGroupsGrid';
import { InventoryItemsModalItemsGrid } from './InventoryItemsModalItemsGrid';
import {
  InventoryItemsModalTagsGrid,
  InventoryTagOption,
} from './InventoryItemsModalTagsGrid';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { InventoryItemRecord } from '~/modules/trade/types/load';

export type InventoryItemsModalRootViewProps = {
  mode: InventoryItemsModalMode;
  effectiveColorScheme: 'light' | 'dark';
  searchTerm: string;
  noResultsFound: boolean;

  // Items mode data
  materialSearchResults: InventoryItemRecord[];
  groupSearchResults: InventoryItemRecord[];

  // Tags mode data
  tagSearchResults: InventoryTagOption[];
  tagGroupSearchResults: InventoryItemRecord[];

  // All groups
  itemGroups: InventoryItemRecord[];
  selectedMaterialGroupId: string | null;

  // Selection state
  selectedId?: string | null;

  // Callbacks
  onSelectGroup: (groupId: string) => void;
  onSelectMaterial: (materialId: string) => void;
  onSelectTag: (tagId: string, tagData: InventoryTagData) => void;
};

export const InventoryItemsModalRootView = ({
  mode,
  effectiveColorScheme: _effectiveColorScheme,
  searchTerm,
  noResultsFound,
  materialSearchResults,
  groupSearchResults,
  tagSearchResults,
  tagGroupSearchResults,
  itemGroups,
  selectedMaterialGroupId,
  selectedId: _selectedId,
  onSelectGroup,
  onSelectMaterial,
  onSelectTag,
}: InventoryItemsModalRootViewProps) => {
  const t = useTranslations();
  const hasItemsSearchResults =
    mode === 'items' &&
    searchTerm.length > 0 &&
    materialSearchResults.length > 0;
  const hasTagsSearchResults =
    mode === 'tags' && searchTerm.length > 0 && tagSearchResults.length > 0;

  const showAllGroups = searchTerm.length === 0 || noResultsFound;

  return (
    <div className="@InventoryItemsModalRootView flex flex-col gap-6">
      {/* 🟡🟡🟡 NO RESULTS FOUND */}
      {noResultsFound && (
        <>
          <div className="flex items-center justify-center gap-3 px-3.5 py-5">
            <LucideFrown size={28} className="text-neutral opacity-30" />
            <span className="font-medium text-neutral/40">
              {t.inventoryItemsModal.noResultsFound}
            </span>
          </div>
          <hr className="m-0 border border-neutral/20" />
        </>
      )}

      {/* 🟡🟡🟡 SEARCH RESULTS - ITEMS MODE */}
      {hasItemsSearchResults && (
        <>
          <div className="flex flex-col gap-4">
            <span className="font-semibold">
              {t.inventoryItemsModal.materialGroups}
            </span>
            <InventoryItemsModalGroupsGrid
              groups={groupSearchResults}
              selectedGroupId={selectedMaterialGroupId}
              onSelectGroup={onSelectGroup}
            />
          </div>
          <div className="flex flex-col gap-4">
            <span className="font-semibold">
              {t.inventoryItemsModal.materials}
            </span>
            <InventoryItemsModalItemsGrid
              items={materialSearchResults}
              onSelectItem={onSelectMaterial}
            />
          </div>
        </>
      )}

      {/* 🟡🟡🟡 SEARCH RESULTS - TAGS MODE */}
      {hasTagsSearchResults && (
        <>
          <div className="flex flex-col gap-4">
            <span className="font-semibold">
              {t.inventoryItemsModal.materialGroups}
            </span>
            <InventoryItemsModalGroupsGrid
              groups={tagGroupSearchResults}
              selectedGroupId={selectedMaterialGroupId}
              onSelectGroup={onSelectGroup}
            />
          </div>
          <div className="flex flex-col gap-4">
            <span className="font-semibold">{t.inventoryItemsModal.tags}</span>
            <InventoryItemsModalTagsGrid
              tags={tagSearchResults}
              onSelectTag={onSelectTag}
            />
          </div>
        </>
      )}

      {/* 🟡🟡🟡 MATERIAL GROUPS LIST */}
      {showAllGroups && (
        <div className="flex flex-col gap-4">
          {noResultsFound && (
            <span className="font-semibold">
              {t.inventoryItemsModal.materialGroups}
            </span>
          )}
          <InventoryItemsModalGroupsGrid
            groups={itemGroups}
            selectedGroupId={selectedMaterialGroupId}
            onSelectGroup={onSelectGroup}
          />
        </div>
      )}
    </div>
  );
};
